import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Plus,
  FileText,
  Check,
  ShieldCheck,
  CreditCard,
  Lock,
  Loader2,
  Pencil,
  Trash2,
  Clock,
  ShieldCheck as ShieldCheckIcon,
  Search,
  Sparkles,
  TrendingUp,
  Wallet,
  Hourglass,
  CheckCircle2,
} from "lucide-react";
import { useCountUp } from "@/hooks/use-count-up";
import { releaseInvoiceFunds } from "@/lib/escrow.functions";
import { useTranslation } from "react-i18next";

import { supabase } from "@/integrations/supabase/client";
import { createInvoiceCheckout, verifyInvoiceSession, syncInvoiceFromStripe } from "@/lib/stripe.functions";

import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/invoices")({
  head: () => ({
    meta: [
      { title: "Invoices — Lumvio" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InvoicesPage,
});

type InvoiceStatus =
  | "pending"
  | "unpaid"
  | "paid"
  | "paid_to_platform"
  | "released_to_freelancer";

interface InvoiceRow {
  id: string;
  project_id: string;
  freelancer_id: string;
  amount: number;
  net_amount: number;
  stripe_fee_amount: number;
  description: string | null;
  status: InvoiceStatus;
  paid_at: string | null;
  released_at: string | null;
  created_at: string;
  auto_release_at: string | null;
  stripe_session_id: string | null;
  escrow_status: "held_in_escrow" | "released" | "disputed" | null;
  projects: { title: string; client_email: string } | null;
}

const STATUS_STYLE: Record<InvoiceStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  unpaid: "bg-muted text-muted-foreground",
  paid: "bg-primary/10 text-primary glow-soft glow-breathe",
  paid_to_platform: "bg-amber-500/10 text-amber-500 glow-soft",
  released_to_freelancer: "bg-success/15 text-success border border-success/30 glow-soft glow-breathe",
};

// Card processing fee shown to clients (deducted from freelancer payout)
const CARD_FEE_PCT = 2.9;
const CARD_FEE_FIXED = 0.30;
const netPayout = (amount: number) =>
  Math.max(0, amount - amount * (CARD_FEE_PCT / 100) - CARD_FEE_FIXED);

function InvoicesPage() {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.language || "en").startsWith("ar");
  const localeCode = isAr ? "ar" : undefined;

  useEffect(() => {
    document.title = `${t("invoices.title")} — Lumvio`;
  }, [t, i18n.language]);

  const statusLabel = (s: InvoiceStatus): string => {
    switch (s) {
      case "pending":
      case "unpaid":
        return t("invoices.status.pending");
      case "paid":
        return t("invoices.status.paid");
      case "paid_to_platform":
        return t("invoices.status.inEscrow");
      case "released_to_freelancer":
        return t("invoices.status.released");
    }
  };

  const { user, profile } = useAuth();
  const qc = useQueryClient();
  const isFreelancer = profile?.role === "freelancer";

  const { data: invoices, isLoading } = useQuery({
    queryKey: ["invoices", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*, projects(title, client_email)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as InvoiceRow[];
    },
    enabled: !!user,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`invoice-status-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "invoices" },
        () => {
          qc.invalidateQueries({ queryKey: ["invoices", user.id] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc, user]);

  const sync = useServerFn(syncInvoiceFromStripe);
  useEffect(() => {
    if (!invoices?.length) return;
    const pending = invoices.filter(
      (i) => (i.status === "pending" || i.status === "unpaid") && i.stripe_session_id,
    );
    if (!pending.length) return;
    let cancelled = false;
    const tick = async () => {
      for (const inv of pending) {
        if (cancelled) return;
        try {
          const r = await sync({ data: { invoice_id: inv.id } });
          if (r?.updated) qc.invalidateQueries({ queryKey: ["invoices"] });
        } catch {
          /* silent */
        }
      }
    };
    const id = window.setInterval(tick, 8000);
    const first = window.setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.clearTimeout(first);
    };
  }, [invoices, sync, qc]);

  const { data: projects } = useQuery({
    queryKey: ["projects", "for-invoice", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, title, client_email")
        .eq("freelancer_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user && isFreelancer,
  });

  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [payingInvoice, setPayingInvoice] = useState<InvoiceRow | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceRow | null>(null);
  const [deletingInvoice, setDeletingInvoice] = useState<InvoiceRow | null>(null);
  const [, setSyncing] = useState(false);
  const verify = useServerFn(verifyInvoiceSession);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid");
    const sid = params.get("sid");
    if (paid !== "1" || !sid) return;

    let cancelled = false;
    setSyncing(true);

    (async () => {
      for (let i = 0; i < 6 && !cancelled; i++) {
        try {
          const res = await verify({ data: { session_id: sid } });
          if (res?.updated) break;
        } catch (e) {
          console.error("verify session failed", e);
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (cancelled) return;
      await qc.invalidateQueries({ queryKey: ["invoices"] });
      setSyncing(false);
      toast.success(t("invoices.toasts.paymentReceived"));
      const url = new URL(window.location.href);
      url.searchParams.delete("paid");
      url.searchParams.delete("sid");
      window.history.replaceState({}, "", url.toString());
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createInvoice = useMutation({
    mutationFn: async () => {
      if (!projectId) throw new Error(t("invoices.toasts.selectProject"));
      const amt = Number(amount);
      if (!amt || amt <= 0) throw new Error(t("invoices.toasts.validAmount"));
      const { error } = await supabase.from("invoices").insert({
        project_id: projectId,
        freelancer_id: user!.id,
        amount: amt,
        description: description || null,
        status: "pending",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("invoices.toasts.created"));
      setOpen(false);
      setProjectId("");
      setAmount("");
      setDescription("");
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteInvoice = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("invoices").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("invoices.toasts.deleted"));
      setDeletingInvoice(null);
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const releaseFn = useServerFn(releaseInvoiceFunds);
  const releaseFunds = useMutation({
    mutationFn: (invoiceId: string) => releaseFn({ data: { invoiceId } }),
    onSuccess: () => {
      toast.success(t("invoices.toasts.releasedToFreelancer"));
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [filter, setFilter] = useState<"all" | "pending" | "escrow" | "released">("all");
  const [query, setQuery] = useState("");

  const stats = useMemo(() => {
    const list = invoices ?? [];
    const sum = (arr: InvoiceRow[], key: "amount" | "net_amount" = "amount") =>
      arr.reduce((s, i) => s + Number(i[key] ?? i.amount ?? 0), 0);
    const pending = list.filter((i) => i.status === "pending" || i.status === "unpaid");
    const escrow = list.filter((i) => i.status === "paid_to_platform");
    const released = list.filter(
      (i) => i.status === "released_to_freelancer" || i.status === "paid",
    );
    return {
      total: sum(list),
      totalCount: list.length,
      pendingAmt: sum(pending),
      pendingCount: pending.length,
      escrowAmt: isFreelancer ? sum(escrow, "net_amount") : sum(escrow),
      escrowCount: escrow.length,
      releasedAmt: isFreelancer ? sum(released, "net_amount") : sum(released),
      releasedCount: released.length,
    };
  }, [invoices, isFreelancer]);

  const filtered = useMemo(() => {
    const list = invoices ?? [];
    const q = query.trim().toLowerCase();
    return list.filter((inv) => {
      if (filter === "pending" && !(inv.status === "pending" || inv.status === "unpaid")) return false;
      if (filter === "escrow" && inv.status !== "paid_to_platform") return false;
      if (filter === "released" && !(inv.status === "released_to_freelancer" || inv.status === "paid")) return false;
      if (!q) return true;
      return (
        (inv.projects?.title ?? "").toLowerCase().includes(q) ||
        (inv.description ?? "").toLowerCase().includes(q) ||
        String(inv.amount).includes(q)
      );
    });
  }, [invoices, filter, query]);

  const totalUp = useCountUp(stats.total);
  const escrowUp = useCountUp(stats.escrowAmt);
  const releasedUp = useCountUp(stats.releasedAmt);
  const pendingUp = useCountUp(stats.pendingAmt);

  const invoicesCount = (n: number) =>
    n === 1
      ? t("invoices.stats.invoicesOne", { count: n })
      : t("invoices.stats.invoicesOther", { count: n });

  return (
    <div className="space-y-8">
      {/* ============ Apple-style Hero ============ */}
      <section className="relative overflow-hidden rounded-[28px] border border-border/60 bg-gradient-to-b from-card to-background p-6 sm:p-10">
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />

        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/60 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              {t("invoices.badgeLive")}
            </div>
            <h1 className="text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-[44px]">
              {t("invoices.title")}.
            </h1>
            <p className="max-w-xl text-[15px] leading-relaxed text-muted-foreground">
              {isFreelancer ? t("invoices.subtitleFreelancer") : t("invoices.subtitleClient")}
            </p>
          </div>
          {isFreelancer && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  size="lg"
                  className="group relative h-11 rounded-full bg-foreground px-5 text-background shadow-sm hover:bg-foreground/90"
                >
                  <Plus className="me-2 h-4 w-4" />
                  {t("invoices.newInvoice")}
                </Button>
              </DialogTrigger>
              <DialogContent className="overflow-hidden border-border/60 bg-card/95 p-0 backdrop-blur-xl sm:max-w-[520px]">
                <div className="relative overflow-hidden border-b border-border/60 px-6 pb-5 pt-6">
                  <div className="pointer-events-none absolute -start-16 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
                  <div className="pointer-events-none absolute -end-10 -top-24 h-52 w-52 rounded-full bg-emerald-400/20 blur-3xl" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
                  <div className="relative flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary shadow-inner">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <DialogHeader className="space-y-1 text-start">
                        <DialogTitle className="text-xl font-semibold tracking-tight">
                          {t("invoices.dialog.title")}
                        </DialogTitle>
                        <p className="text-xs text-muted-foreground">
                          {t("invoices.dialog.subtitle")}
                        </p>
                      </DialogHeader>
                    </div>
                  </div>
                </div>

                <div className="space-y-5 px-6 py-5">
                  <div className="space-y-2">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("invoices.dialog.project")}
                    </Label>
                    <Select value={projectId} onValueChange={setProjectId}>
                      <SelectTrigger className="h-11 rounded-xl border-border/60 bg-background/60">
                        <SelectValue placeholder={t("invoices.dialog.selectProject")} />
                      </SelectTrigger>
                      <SelectContent>
                        {projects?.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.title} — {p.client_email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="amount" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("invoices.dialog.amount")}
                    </Label>
                    <div className="group relative" dir="ltr">
                      <span className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-muted-foreground group-focus-within:text-primary">
                        $
                      </span>
                      <Input
                        id="amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder={t("invoices.dialog.amountPlaceholder")}
                        className="h-14 rounded-xl border-border/60 bg-background/60 ps-9 pe-16 text-2xl font-semibold tracking-tight tabular-nums"
                      />
                      <span className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 rounded-md border border-border/60 bg-muted/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("invoices.currency.usd")}
                      </span>
                    </div>

                    {(() => {
                      const gross = Math.max(0, parseFloat(amount || "0"));
                      const fee = gross > 0 ? +(gross * 0.029 + 0.3).toFixed(2) : 0;
                      const net = Math.max(0, +(gross - fee).toFixed(2));
                      return (
                        <div className="mt-3 rounded-xl border border-border/60 bg-gradient-to-br from-muted/40 to-transparent p-3">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{t("invoices.preview.clientPays")}</span>
                            <span className="font-semibold tabular-nums" dir="ltr">${gross.toFixed(2)}</span>
                          </div>
                          <div className="mt-1 flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{t("invoices.preview.cardFee")}</span>
                            <span className="tabular-nums text-muted-foreground" dir="ltr">− ${fee.toFixed(2)}</span>
                          </div>
                          <div className="my-2 h-px bg-border/60" />
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1.5 font-medium">
                              <Wallet className="h-3.5 w-3.5 text-emerald-400" />
                              {t("invoices.preview.youReceive")}
                            </span>
                            <span className="font-bold tabular-nums text-emerald-400" dir="ltr">
                              ${net.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="desc" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("invoices.dialog.description")}{" "}
                      <span className="ms-1 font-normal normal-case text-muted-foreground/60">
                        {t("invoices.dialog.optional")}
                      </span>
                    </Label>
                    <Textarea
                      id="desc"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("invoices.dialog.descriptionPlaceholder")}
                      rows={3}
                      className="resize-none rounded-xl border-border/60 bg-background/60"
                    />
                  </div>
                </div>

                <DialogFooter className="flex-col-reverse gap-2 border-t border-border/60 bg-muted/20 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    {t("invoices.dialog.footerNote")}
                  </span>
                  <Button
                    onClick={() => createInvoice.mutate()}
                    disabled={createInvoice.isPending || !projectId || !amount}
                    className="group relative h-11 min-w-[160px] overflow-hidden rounded-xl bg-gradient-to-r from-primary to-emerald-500 shadow-lg shadow-primary/25 hover:shadow-primary/40"
                  >
                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full rtl:translate-x-full rtl:group-hover:-translate-x-full" />
                    {createInvoice.isPending ? (
                      <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="me-2 h-4 w-4" />
                    )}
                    {t("invoices.dialog.submit")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stat tiles */}
        <div className="relative mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border/60 bg-border/60 lg:grid-cols-4">
          <StatTile
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            label={isFreelancer ? t("invoices.stats.totalInvoiced") : t("invoices.stats.totalBilled")}
            value={`$${totalUp.toFixed(2)}`}
            sub={invoicesCount(stats.totalCount)}
            tone="primary"
          />
          <StatTile
            icon={<Hourglass className="h-3.5 w-3.5" />}
            label={t("invoices.stats.pending")}
            value={`$${pendingUp.toFixed(2)}`}
            sub={t("invoices.stats.awaitingPayment", { count: stats.pendingCount })}
            tone="amber"
          />
          <StatTile
            icon={<Lock className="h-3.5 w-3.5" />}
            label={t("invoices.stats.inEscrow")}
            value={`$${escrowUp.toFixed(2)}`}
            sub={t("invoices.stats.heldSafely", { count: stats.escrowCount })}
            tone="sky"
            pulse={stats.escrowCount > 0}
          />
          <StatTile
            icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            label={isFreelancer ? t("invoices.stats.paidOut") : t("invoices.stats.released")}
            value={`$${releasedUp.toFixed(2)}`}
            sub={t("invoices.stats.completed", { count: stats.releasedCount })}
            tone="emerald"
          />
        </div>
      </section>

      {isFreelancer && (
        <div className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-background text-muted-foreground">
              <CreditCard className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold tracking-tight text-foreground">
                {t("invoices.feeCard.title")}
              </div>
              <div className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">
                {t("invoices.feeCard.body")}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ Sticky filter bar ============ */}
      <div className="sticky top-2 z-20 -mx-2 rounded-2xl border border-border/60 bg-background/70 px-2 py-2 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex items-center gap-0.5 rounded-full border border-border/60 bg-muted/40 p-0.5">
            {([
              { id: "all", label: t("invoices.filters.all"), count: stats.totalCount },
              { id: "pending", label: t("invoices.filters.pending"), count: stats.pendingCount },
              { id: "escrow", label: t("invoices.filters.inEscrow"), count: stats.escrowCount },
              { id: "released", label: t("invoices.filters.released"), count: stats.releasedCount },
            ] as const).map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-all " +
                    (active
                      ? "bg-foreground text-background shadow-sm"
                      : "text-muted-foreground hover:text-foreground")
                  }
                >
                  {f.label}
                  <span
                    className={
                      "rounded-full px-1.5 py-0 text-[10px] tabular-nums " +
                      (active ? "bg-background/20 text-background" : "bg-foreground/10")
                    }
                  >
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("invoices.searchPlaceholder")}
              className="h-9 rounded-full border-border/60 bg-muted/40 ps-9 text-xs"
            />
          </div>
        </div>
      </div>

      {/* ============ Invoice list ============ */}
      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl border bg-muted/40" />
          ))}
        </div>
      ) : !filtered.length ? (
        <div className="relative overflow-hidden rounded-2xl border border-dashed p-14 text-center">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{ background: "radial-gradient(circle at center, hsl(var(--primary)/0.08), transparent 70%)" }}
          />
          <div className="relative">
            <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <FileText className="h-6 w-6" />
              <span className="absolute inset-0 animate-ping rounded-2xl bg-primary/10" />
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              {invoices?.length ? t("invoices.noMatch") : t("invoices.empty")}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((inv, idx) => {
            const isOpen = inv.status === "pending" || inv.status === "unpaid";
            const label =
              !isFreelancer &&
              (inv.status === "paid_to_platform" || inv.status === "released_to_freelancer")
                ? t("invoices.status.paid")
                : statusLabel(inv.status);
            const style =
              !isFreelancer && inv.status === "paid_to_platform"
                ? "bg-success/15 text-success border border-success/30"
                : (STATUS_STYLE[inv.status] + " border border-transparent");
            const accent =
              inv.status === "released_to_freelancer" || inv.status === "paid"
                ? "from-success/60"
                : inv.status === "paid_to_platform"
                ? "from-sky-500/60"
                : "from-amber-500/60";
            return (
              <div
                key={inv.id}
                className="group relative overflow-hidden rounded-2xl border bg-card p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 sm:p-5"
                style={{ animation: `fadeInUp 0.5s ease-out ${idx * 60}ms both` }}
              >
                <span
                  aria-hidden
                  className={`absolute inset-y-0 start-0 w-1 bg-gradient-to-b ${accent} to-transparent`}
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  style={{ background: "radial-gradient(400px circle at var(--x,50%) var(--y,50%), hsl(var(--primary)/0.06), transparent 60%)" }}
                />

                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="truncate font-semibold">
                          {inv.projects?.title ?? t("invoices.untitled")}
                        </div>
                        <span
                          className={
                            "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide " +
                            style
                          }
                        >
                          {label}
                        </span>
                      </div>
                      <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                        {inv.description ?? "—"}
                      </div>
                      <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground/70">
                        {new Date(inv.created_at).toLocaleDateString(localeCode, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="text-end font-mono sm:min-w-[160px]">
                    {isFreelancer && (inv.status === "paid_to_platform" || inv.status === "released_to_freelancer") ? (
                      <>
                        <div className="text-lg font-semibold tabular-nums" dir="ltr">
                          ${Number(inv.net_amount ?? inv.amount).toFixed(2)}
                        </div>
                        <div className="text-[10px] font-sans text-muted-foreground">
                          {t("invoices.row.grossMinusFee", {
                            gross: Number(inv.amount).toFixed(2),
                            fee: Number(inv.stripe_fee_amount ?? 0).toFixed(2),
                          })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-lg font-semibold tabular-nums" dir="ltr">
                          ${Number(inv.amount).toFixed(2)}
                        </div>
                        {!isFreelancer && (inv.status === "pending" || inv.status === "unpaid") && (
                          <div className="text-[10px] font-sans text-muted-foreground">
                            {t("invoices.row.freelancerNets", { net: netPayout(Number(inv.amount)).toFixed(2) })}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-2 sm:min-w-[220px]">
                    {isOpen && !isFreelancer && (
                      <Button size="sm" onClick={() => setPayingInvoice(inv)}>
                        <CreditCard className="me-2 h-3.5 w-3.5" />
                        {t("invoices.actions.pay")}
                      </Button>
                    )}
                    {isOpen && isFreelancer && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditingInvoice(inv)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDeletingInvoice(inv)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </>
                    )}
                    {isFreelancer && inv.status === "paid_to_platform" && (
                      <div className="flex flex-col items-end gap-1">
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-500">
                          <Lock className="h-3 w-3" /> {t("invoices.actions.inEscrow")}
                        </span>
                        {inv.auto_release_at && (
                          <EscrowCountdown target={inv.auto_release_at} tone="freelancer" />
                        )}
                      </div>
                    )}
                    {isFreelancer && inv.status === "released_to_freelancer" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-1 text-xs font-medium text-success border border-success/30">
                        <Check className="h-3 w-3" /> {t("invoices.actions.released")}
                      </span>
                    )}
                    {!isFreelancer && inv.status === "paid_to_platform" && (
                      <div className="flex flex-col items-end gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => releaseFunds.mutate(inv.id)}
                          disabled={releaseFunds.isPending}
                          className="bg-emerald-600 text-white shadow-md shadow-emerald-500/30 hover:bg-emerald-700"
                        >
                          <ShieldCheckIcon className="me-1.5 h-3.5 w-3.5" />
                          {t("invoices.actions.confirmRelease")}
                        </Button>
                        {inv.auto_release_at && (
                          <EscrowCountdown target={inv.auto_release_at} tone="client" />
                        )}
                      </div>
                    )}
                    {!isFreelancer &&
                      (inv.status === "released_to_freelancer" || inv.status === "paid") && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-1 text-xs font-medium text-success border border-success/30">
                          <Check className="h-3 w-3" /> {t("invoices.actions.paid")}
                        </span>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CheckoutDialog
        invoice={payingInvoice}
        onClose={() => setPayingInvoice(null)}
        onPaid={() => {
          setPayingInvoice(null);
          qc.invalidateQueries({ queryKey: ["invoices"] });
        }}
      />

      <EditInvoiceDialog
        invoice={editingInvoice}
        onClose={() => setEditingInvoice(null)}
        onSaved={() => {
          setEditingInvoice(null);
          qc.invalidateQueries({ queryKey: ["invoices"] });
        }}
      />

      <Dialog open={!!deletingInvoice} onOpenChange={(o) => !o && setDeletingInvoice(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("invoices.delete.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deletingInvoice
              ? t("invoices.delete.bodyWithAmount", { amount: Number(deletingInvoice.amount).toFixed(2) })
              : t("invoices.delete.bodyPlain")}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeletingInvoice(null)}>
              {t("invoices.delete.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingInvoice && deleteInvoice.mutate(deletingInvoice.id)}
              disabled={deleteInvoice.isPending}
            >
              {deleteInvoice.isPending ? t("invoices.delete.deleting") : t("invoices.delete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditInvoiceDialog({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: InvoiceRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  if (invoice && amount === "" && description === "") {
    setAmount(String(invoice.amount));
    setDescription(invoice.description ?? "");
  }

  const save = async () => {
    if (!invoice) return;
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error(t("invoices.toasts.validAmount"));
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("invoices")
      .update({ amount: amt, description: description || null })
      .eq("id", invoice.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("invoices.toasts.updated"));
    setAmount("");
    setDescription("");
    onSaved();
  };

  const handleClose = () => {
    setAmount("");
    setDescription("");
    onClose();
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("invoices.edit.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-amount">{t("invoices.edit.amount")}</Label>
            <Input
              id="edit-amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-desc">{t("invoices.edit.description")}</Label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>
            {t("invoices.edit.cancel")}
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? t("invoices.edit.saving") : t("invoices.edit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckoutDialog({
  invoice,
  onClose,
  onPaid: _onPaid,
}: {
  invoice: InvoiceRow | null;
  onClose: () => void;
  onPaid: () => void;
}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const startCheckout = useServerFn(createInvoiceCheckout);

  const pay = async () => {
    if (!invoice) return;
    setProcessing(true);
    try {
      const { url } = await startCheckout({ data: { invoice_id: invoice.id } });
      if (!url) throw new Error(t("invoices.toasts.noCheckoutUrl"));
      window.location.href = url;
    } catch (e) {
      setProcessing(false);
      toast.error(e instanceof Error ? e.message : t("invoices.toasts.checkoutFailed"));
    }
  };

  const amount = invoice ? Number(invoice.amount) : 0;
  const net = invoice ? netPayout(amount) : 0;
  const fee = amount - net;

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg overflow-hidden p-0 border-border/60">
        <div className="relative overflow-hidden border-b border-border/60 bg-gradient-to-br from-primary/10 via-background to-emerald-500/10 px-6 pt-6 pb-5">
          <div className="pointer-events-none absolute -top-16 -end-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -start-10 h-48 w-48 rounded-full bg-emerald-500/20 blur-3xl" />
          <DialogHeader className="relative space-y-2 text-start">
            <div className="inline-flex w-fit items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              <ShieldCheck className="h-3 w-3" /> {t("invoices.checkout.badge")}
            </div>
            <DialogTitle className="text-2xl font-semibold tracking-tight">
              {t("invoices.checkout.title")}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              {t("invoices.checkout.subtitle")}
            </p>
          </DialogHeader>
        </div>

        {invoice && (
          <div className="space-y-4 px-6 py-5">
            <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-muted/40 to-background p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {invoice.projects?.title ?? t("invoices.checkout.invoiceFallback")}
                  </div>
                  <div className="mt-1 truncate text-sm text-foreground/80">
                    {invoice.description ?? t("invoices.checkout.descFallback")}
                  </div>
                </div>
                <div className="text-end">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("invoices.checkout.total")}
                  </div>
                  <div className="font-serif text-3xl font-semibold tabular-nums leading-none" dir="ltr">
                    ${amount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border/60 bg-card p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary ring-1 ring-primary/20">
                  <CreditCard className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{t("invoices.checkout.methods")}</div>
                  <div className="text-xs text-muted-foreground">
                    {t("invoices.checkout.stripeNote")}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> {t("invoices.checkout.tls")}</span>
                <span className="text-border">•</span>
                <span>{t("invoices.checkout.pci")}</span>
                <span className="text-border">•</span>
                <span>{t("invoices.checkout.neverSeeCard")}</span>
              </div>
            </div>

            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
              <div className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {t("invoices.checkout.breakdown")}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t("invoices.checkout.youPay")}</span>
                  <span className="tabular-nums font-medium" dir="ltr">${amount.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t("invoices.checkout.stripeFee", { pct: CARD_FEE_PCT, fixed: CARD_FEE_FIXED.toFixed(2) })}</span>
                  <span className="tabular-nums" dir="ltr">−${fee.toFixed(2)}</span>
                </div>
                <div className="border-t border-border/60 pt-2 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t("invoices.checkout.freelancerReceives")}</span>
                  <span className="tabular-nums font-semibold text-emerald-600 dark:text-emerald-400" dir="ltr">
                    ${net.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-transparent p-3.5 text-xs">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <ShieldCheck className="h-3.5 w-3.5" />
              </div>
              <p className="text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground">{t("invoices.checkout.protectedTitle")}</span>{" "}
                {t("invoices.checkout.protectedBody")}
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="border-t border-border/60 bg-muted/20 px-6 py-4 gap-2 sm:justify-between">
          <Button variant="ghost" onClick={onClose} disabled={processing}>
            {t("invoices.checkout.cancel")}
          </Button>
          <Button
            onClick={pay}
            disabled={processing}
            className="relative overflow-hidden bg-gradient-to-r from-primary via-primary to-emerald-500 text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
          >
            {processing ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
                {t("invoices.checkout.processing")}
              </>
            ) : (
              <>
                <Lock className="me-2 h-3.5 w-3.5" />
                {t("invoices.checkout.pay", { amount: amount.toFixed(2) })}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EscrowCountdown({ target, tone }: { target: string; tone: "client" | "freelancer" }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-success">
        <Clock className="h-3 w-3" /> {t("invoices.countdown.autoReleasing")}
      </span>
    );
  }
  const hours = Math.floor(diff / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const label =
    tone === "client"
      ? t("invoices.countdown.clientLabel", { h: hours, m: mins })
      : t("invoices.countdown.freelancerLabel", { h: hours, m: mins });
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-500">
      <Clock className="h-3 w-3" /> {label}
    </span>
  );
}

function StatTile({
  icon,
  label,
  value,
  sub,
  tone,
  pulse,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: "primary" | "amber" | "sky" | "emerald";
  pulse?: boolean;
}) {
  const dotMap: Record<string, string> = {
    primary: "bg-primary",
    amber: "bg-amber-500",
    sky: "bg-sky-500",
    emerald: "bg-emerald-500",
  };
  const dot = dotMap[tone];
  return (
    <div className="relative bg-card p-5 transition-colors">
      <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />
      <div className="flex items-center justify-between">
        <div className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 bg-background text-muted-foreground">
          {icon}
        </div>
        {pulse ? (
          <span className="relative flex h-1.5 w-1.5">
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-70 ${dot}`} />
            <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${dot}`} />
          </span>
        ) : (
          <span className={`h-1.5 w-1.5 rounded-full ${dot} opacity-70`} />
        )}
      </div>
      <div className="mt-4 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1.5 text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-foreground" dir="ltr">
        {value}
      </div>
      <div className="mt-1.5 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}
